import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import { format, parseISO } from "date-fns";
import * as apicall from "../../components/Apicall";
import Loader from "../../components/Loader";

const STATUS_BADGE = {
  active: "bg-primary",
  paid: "bg-success",
  expired: "bg-secondary",
  cancelled: "bg-dark",
  failed: "bg-danger",
};
const STATUS_LABEL = {
  active: "Active",
  paid: "Paid",
  expired: "Expired",
  cancelled: "Cancelled",
  failed: "Failed",
};
const GATEWAY_LABEL = { stripe: "Stripe", razorpay: "Razorpay" };

const errorText = (e) =>
  (e?.message || "Something went wrong").replace("Error:", "").trim();

const fmt = (iso) => {
  try {
    return format(parseISO(iso), "dd MMM yyyy, hh:mm a");
  } catch {
    return "-";
  }
};

// Mirrors the backend rule: India (current_country 2) -> Razorpay/INR, otherwise Stripe/AED.
const gatewayForCountry = (country) =>
  Number(country) === 2
    ? { gateway: "razorpay", currency: "INR" }
    : { gateway: "stripe", currency: "AED" };

const PaymentLinkSection = ({ booking, isAdmin, onBookingUpdated }) => {
  const bookingId = booking?.id;
  const defaultAmount = booking?.user_total_payable_amount;

  const [link, setLink] = useState(null);
  const [gateway, setGateway] = useState(gatewayForCountry(booking?.current_country));
  const [amount, setAmount] = useState(defaultAmount ?? "");
  const [initialLoading, setInitialLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  useEffect(() => {
    setAmount(defaultAmount ?? "");
  }, [defaultAmount]);

  const applyPaidNotice = (data) => {
    if (data?.booking_updated) {
      toast.success("Payment received. Booking marked as Paid online.");
      if (onBookingUpdated) setTimeout(onBookingUpdated, 1500);
    }
  };

  // Asks the backend for the latest link; the backend also checks the gateway.
  const loadLink = useCallback(
    async ({ manual = false } = {}) => {
      if (!bookingId) return;
      manual ? setRefreshing(true) : setInitialLoading(true);
      try {
        const res = await apicall.get(`admin_payment_link/${bookingId}`);
        if (res?.data) {
          setLink(res.data.link);
          if (res.data.gateway) setGateway(res.data.gateway);
          applyPaidNotice(res.data);
        }
      } catch (e) {
        if (manual) toast.error(errorText(e));
        else console.error("Payment link load failed", e);
      } finally {
        manual ? setRefreshing(false) : setInitialLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookingId]
  );

  useEffect(() => {
    loadLink();
  }, [loadLink]);

  const hasActiveLink = link?.status === "active";

  const handleCreate = async () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error("Enter an amount greater than 0");
      return;
    }
    setCreating(true);
    try {
      const res = await apicall.post(
        { booking_id: bookingId, amount: value },
        "/admin_create_payment_link"
      );
      if (res?.data?.link) setLink(res.data.link);
      if (res?.data?.already_exists) {
        toast.info("An active payment link already exists for this booking");
      } else {
        toast.success("Payment link created");
      }
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      const res = await apicall.post(
        { booking_id: bookingId },
        "/admin_cancel_payment_link"
      );
      if (res?.data?.link) setLink(res.data.link);
      toast.success("Payment link cancelled. You can create a new one.");
      applyPaidNotice(res?.data);
    } catch (e) {
      toast.error(errorText(e));
      loadLink({ manual: true });
    } finally {
      setCancelling(false);
      setShowCancel(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy. Select the link and copy it manually.");
    }
  };

  return (
    <>
      <hr className="my-3" />
      <div className="row">
        <div className="col-md-12 mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h1 className="label mb-0">Payment Link</h1>
            <span className="content">
              {GATEWAY_LABEL[gateway.gateway]} ({gateway.currency})
            </span>
          </div>

          {isAdmin && !hasActiveLink && (
            <div className="d-flex align-items-center mb-2">
              <label className="label me-2 mb-0" htmlFor="payment-link-amount">
                Amount ({gateway.currency})
              </label>
              <input
                id="payment-link-amount"
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={{
                  width: "150px",
                  padding: "6px 10px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
              <button
                className="btn Button primaryBackground text-white ms-3 d-flex justify-content-center align-items-center"
                onClick={handleCreate}
                disabled={creating || initialLoading}
              >
                {creating ? (
                  <Loader style={{ height: "30", width: "40" }} />
                ) : (
                  "Create Payment Link"
                )}
              </button>
            </div>
          )}

          {!isAdmin && !hasActiveLink && (
            <p className="content text-muted">
              Only admin users can create payment links.
            </p>
          )}

          {initialLoading && <p className="content">Loading payment link...</p>}

          {!initialLoading && !link && (
            <p className="content">No payment link created yet.</p>
          )}

          {link && (
            <div>
              <div className="d-flex align-items-center mb-2">
                <span className={`badge ${STATUS_BADGE[link.status] || "bg-secondary"} me-2`}>
                  {STATUS_LABEL[link.status] || link.status}
                </span>
                <span className="content">
                  {link.currency} {Number(link.amount).toFixed(2)}
                </span>
              </div>

              {link.url ? (
                <div className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    value={link.url}
                    readOnly
                    onFocus={(e) => e.target.select()}
                  />
                  <button className="btn btn-outline-secondary" onClick={handleCopy}>
                    Copy
                  </button>
                  <a
                    className="btn btn-outline-secondary"
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open
                  </a>
                </div>
              ) : (
                link.status === "active" && (
                  <p className="content">Link is being created. Refresh status in a moment.</p>
                )
              )}

              {link.status === "active" && (
                <p className="content mb-2">Expires {fmt(link.expires_at)}</p>
              )}
              {link.status === "paid" && (
                <p className="content text-success mb-2">
                  Paid {link.currency} {Number(link.paid_amount ?? link.amount).toFixed(2)} on{" "}
                  {fmt(link.paid_at)}
                </p>
              )}
              {link.status === "expired" && (
                <p className="content mb-2">Expired on {fmt(link.expires_at)}</p>
              )}

              <div className="d-flex">
                <button
                  className="btn btn-outline-secondary btn-sm me-2"
                  onClick={() => loadLink({ manual: true })}
                  disabled={refreshing}
                >
                  {refreshing ? "Refreshing..." : "Refresh status"}
                </button>
                {isAdmin && hasActiveLink && (
                  <button
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => setShowCancel(true)}
                  >
                    Cancel &amp; regenerate
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal show={showCancel} onHide={() => setShowCancel(false)} centered size="xxl">
        <div className="d-flex flex-column justify-content-center align-items-center p-1 m-5">
          <p className="text-center mb-3">
            Cancel this payment link? The customer will no longer be able to pay
            with it, and you can create a new one.
          </p>
          <div className="d-flex justify-content-center align-items-center">
            <button
              className="mx-1 btn Button primaryColorText btn-outline-secondary me-2 no-hover-effect"
              onClick={() => setShowCancel(false)}
            >
              Keep link
            </button>
            <button
              className="btn Button primaryBackground text-white position-relative d-flex justify-content-center align-items-center"
              onClick={handleCancel}
            >
              {cancelling ? (
                <Loader style={{ height: "30", width: "40" }} />
              ) : (
                "Cancel link"
              )}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default PaymentLinkSection;
